import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { PictureStyleRead } from '../../types/api';
import { fetchPictureStyles } from './generationApi';

/**
 * The picture-style registry, loaded once per session.
 *
 * Cached in a module-level promise exactly like `useIntents`: the brief form, the knowledge base and the review screen
 * share one request. A failed load clears the cache, so the next mount really
 * retries.
 */
let pictureStylesPromise: Promise<PictureStyleRead[]> | null = null;

function loadPictureStyles(): Promise<PictureStyleRead[]> {
  if (pictureStylesPromise === null) {
    pictureStylesPromise = fetchPictureStyles().catch((caught: unknown) => {
      pictureStylesPromise = null;
      throw caught;
    });
  }
  return pictureStylesPromise;
}

export interface PictureStylesResult {
  /** The styles in registry order. Empty while loading or on failure. */
  pictureStyles: PictureStyleRead[];
  isLoading: boolean;
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_STYLES: never[] = [];

/**
 * The picture styles behind the brief's "Picture style" field, the brand's
 * style preferences and the review tags.
 *
 * A failure is not fatal anywhere: the style is optional on a brief, so a
 * surface that cannot read the list offers no picker and the run is the AI's
 * choice.
 */
export function usePictureStyles(): PictureStylesResult {
  const [loaded, setLoaded] = useState<PictureStyleRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const styles = await loadPictureStyles();
        if (cancelled) return;
        setLoaded(styles);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setLoaded(NO_STYLES);
        setError(getErrorMessage(caught, 'Could not load the list of picture styles.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    pictureStyles: loaded ?? NO_STYLES,
    isLoading: loaded === null,
    error,
  };
}
